import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Lock, FileText, Server, Network, BookOpen, ArrowRight, CheckCircle2,
  Clock, AlertTriangle,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { BaaCounterStrip } from '../components/brand/BaaCounterStrip';
import { TrustCustomerLogosStrip } from '../components/brand/TrustCustomerLogosStrip';

/**
 * /trust — Trust Portal.
 *
 * CaraLoom 2.0 Blueprint Decision #37 (S:4, C:3, R:3, D:2, T:1, B:4).
 *
 * Public-facing procurement-diligence single page: BAA chain, SOC 2
 * status, HITRUST status, subprocessor list. Hospital procurement teams
 * pull from this page before ever reading a pitch deck. Linked from the
 * footer + /trust-safety + /ai-safety.
 *
 * IMPORTANT: every status string on this page is the literal current
 * truth. No "claims under review" or wishful thinking. Honesty is the
 * brand asset here.
 */

const STATUS_COLOR = {
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  signed:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  scheduled:   'bg-cn-bone-100 text-cn-text-muted border-cn-border',
  pending:     'bg-cn-bone-100 text-cn-text-muted border-cn-border',
  active:      'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const StatusPill = ({ status, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${STATUS_COLOR[status] || STATUS_COLOR.pending}`}
    data-testid={`status-pill-${status}`}
  >
    {status === 'signed' || status === 'active' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
    {label}
  </span>
);

const CERTIFICATIONS = [
  {
    name: 'SOC 2 Type 1',
    description: 'Security, availability, confidentiality, processing integrity, privacy.',
    status: 'in_progress',
    statusLabel: 'Audit engaged',
    detail: 'Vendor selected (Vanta). 4–6 month timeline. Report expected in production calendar Q2 2026.',
  },
  {
    name: 'SOC 2 Type 2',
    description: 'Operational effectiveness of SOC 2 controls over a 6–12 month window.',
    status: 'scheduled',
    statusLabel: 'Q4 2026',
    detail: 'Begins immediately after Type 1 attestation closes.',
  },
  {
    name: 'HITRUST CSF',
    description: 'Healthcare-specific control framework required by enterprise hospital + payer procurement.',
    status: 'scheduled',
    statusLabel: 'Q1 2027',
    detail: '12–18 month engagement. Required for first payer contract; not required for provider SaaS.',
  },
  {
    name: 'HIPAA Posture',
    description: 'Administrative, physical, and technical safeguards under 45 CFR § 164.',
    status: 'active',
    statusLabel: 'Operational',
    // QA iter 120 fix — honest posture until every subprocessor BAA is
    // executed. The blanket "BAAs in place with every subprocessor that
    // touches PHI" claim was replaced with the truthful in-progress line
    // below. The subprocessor table's per-vendor BAA column is the
    // source of truth; procurement should read that, not this summary.
    detail: 'PHI minimization, audit trails, encryption at rest + in transit, role-based access. Subprocessor BAAs are being executed as we onboard the discharge-core pilot — current per-vendor status is in the table below.',
  },
];

// Subprocessor list — every external service that may receive CaraLoom data.
// QA iter 120 fix — honest state: BAA column reflects negotiation
// status. Values: 'Signed' (executed), 'Pending' (in negotiation),
// 'N/A — no PHI' (never receives PHI). NEVER show 'Yes' until we have
// a countersigned copy on file. See routes/trust_baa.py for the
// backend truth-source that this table shadows.
const SUBPROCESSORS = [
  { name: 'MongoDB Atlas',          purpose: 'Primary application database',                 region: 'us-east (AWS)', baa: 'Pending',       phi: 'Yes' },
  { name: 'Amazon Web Services',    purpose: 'Compute, networking, object storage',          region: 'us-east-1',     baa: 'Pending',       phi: 'Yes' },
  { name: 'Google Vertex AI',       purpose: 'LLM inference (Healthcare API tier)',          region: 'us-central1',   baa: 'Pending',       phi: 'Yes' },
  { name: 'Anthropic (via Bedrock)',purpose: 'Claude inference for clinical surfaces',       region: 'us-east-1',     baa: 'Pending',       phi: 'Yes' },
  { name: 'OpenAI (via Azure)',     purpose: 'GPT inference for clinical surfaces',          region: 'East US',       baa: 'Pending',       phi: 'Yes' },
  { name: 'Stripe',                 purpose: 'Payments + Stripe Connect payouts',            region: 'US',            baa: 'N/A — no PHI',  phi: 'No (financial only)' },
  { name: 'Resend',                 purpose: 'Transactional email (de-identified bodies)',   region: 'US',            baa: 'N/A — no PHI',  phi: 'No (PHI scrubbed before send)' },
  { name: 'Twilio',                 purpose: 'SMS + voice notifications',                    region: 'US',            baa: 'Pending',       phi: 'Minimal (initials + appointment link)' },
  { name: 'Daily.co',               purpose: 'Telehealth video infrastructure',              region: 'US',            baa: 'Pending',       phi: 'Yes (ephemeral)' },
  { name: 'Stedi',                  purpose: 'EDI 837P/835 clearinghouse',                   region: 'US',            baa: 'Pending',       phi: 'Yes' },
  { name: 'Checkr',                 purpose: 'Background screening of clinicians',           region: 'US',            baa: 'Pending',       phi: 'No (clinician PII, not patient PHI)' },
  { name: 'Availity / CAQH ProView',purpose: 'Credentialing roster + payer enrollment',      region: 'US',            baa: 'Pending',       phi: 'No (clinician credentials)' },
];

const PRINCIPLES = [
  { icon: ShieldCheck, title: 'PHI by exception, not default',  body: 'Every internal service receives the minimum PHI required to do its job. Email bodies are scrubbed before send. Audit rows record every read of a patient record.' },
  { icon: Lock,        title: 'Encryption everywhere',          body: 'TLS 1.2+ in transit, AES-256 at rest. Database backups are encrypted with customer-managed keys.' },
  { icon: Network,     title: 'Row-level multi-tenancy',        body: 'Every collection is scoped by organization_id. Penetration-tested. No cross-tenant query path exists in code.' },
  { icon: FileText,    title: 'BAA chain transparency',         body: 'The subprocessor table below is the single source of truth — updated within 7 days of any new vendor onboarding.' },
];

export default function TrustPortalPage() {
  return (
    <div className="min-h-screen bg-cn-bg" data-testid="trust-portal-page">
      <Seo
        title="Trust Portal — SOC 2, HITRUST, BAA chain | CaraLoom"
        description="CaraLoom's procurement-diligence portal. SOC 2 status, HITRUST roadmap, the full subprocessor list, and the BAA chain. Updated within 7 days of any change."
        path="/trust"
        keywords={['CaraLoom trust', 'SOC 2', 'HITRUST', 'HIPAA', 'BAA chain', 'subprocessors']}
      />

      <PublicNavbar />

      {/* HERO */}
      <section className="cn-section pt-20 pb-12 lg:pt-28 border-b border-cn-border">
        <div className="cn-container max-w-5xl">
          <div className="inline-flex items-center gap-2 cn-trust-badge mb-5" data-testid="trust-eyebrow">
            <ShieldCheck className="w-3.5 h-3.5 text-cn-primary" />
            Procurement-diligence portal
          </div>
          <h1
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-cn-text dark:text-white leading-[1.05] tracking-tight"
            data-testid="trust-headline"
          >
            Trust Portal
          </h1>
          <p className="mt-5 text-lg text-cn-text-muted leading-relaxed max-w-3xl">
            Every certification, BAA, and subprocessor CaraLoom relies on — updated within
            7 days of any change. If a row says &ldquo;scheduled,&rdquo; it means the date is real and
            the engagement is funded. We don&rsquo;t claim what we don&rsquo;t have.
          </p>
          <p className="mt-3 text-sm text-cn-text-muted/80">
            Need the audit-ready vendor packet? Email{' '}
            <a href="mailto:trust@caraloom.com" className="text-cn-primary font-semibold" data-testid="trust-email-link">
              trust@caraloom.com
            </a>{' '}— we send the SBOM, network diagram, and pen-test summary on request.
          </p>
        </div>
      </section>

      {/* LIVE BAA-COUNTER STRIP */}
      <BaaCounterStrip />

      {/* CUSTOMER LOGOS + UPTIME SLA — Blueprint #44 */}
      <TrustCustomerLogosStrip />

      {/* CERTIFICATIONS */}
      <section className="cn-section py-16" data-testid="trust-certifications">
        <div className="cn-container max-w-5xl">
          <div className="cn-eyebrow mb-3">Certifications &amp; posture</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-cn-text dark:text-white mb-8">
            Where we are, where we&rsquo;re going
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {CERTIFICATIONS.map((c) => (
              <div key={c.name} className="cn-card p-6" data-testid={`cert-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">{c.name}</h3>
                  <StatusPill status={c.status} label={c.statusLabel} />
                </div>
                <p className="text-sm text-cn-text-muted leading-relaxed mb-3">{c.description}</p>
                <p className="text-xs text-cn-text leading-relaxed">{c.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="cn-section py-16 bg-cn-bone-100/40 dark:bg-cn-ink-800/30 border-y border-cn-border">
        <div className="cn-container max-w-5xl">
          <div className="cn-eyebrow mb-3">How we think about data</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-cn-text dark:text-white mb-8">
            Four principles, no asterisks
          </h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="trust-principles">
            {PRINCIPLES.map((p) => {
              const I = p.icon;
              return (
                <div key={p.title} className="cn-card p-6 flex gap-4">
                  <span className="w-10 h-10 shrink-0 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                    <I className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-base text-cn-text dark:text-white mb-1">{p.title}</h3>
                    <p className="text-sm text-cn-text-muted leading-relaxed">{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SUBPROCESSORS TABLE */}
      <section className="cn-section py-16" data-testid="trust-subprocessors">
        <div className="cn-container max-w-6xl">
          <div className="cn-eyebrow mb-3">Subprocessor list</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-cn-text dark:text-white mb-3">
            Every vendor that may receive CaraLoom data
          </h2>
          <p className="text-sm text-cn-text-muted mb-6 max-w-3xl">
            We sign a BAA with every subprocessor that may receive PHI. Those BAAs are
            currently being negotiated as we onboard the discharge-core pilot — the
            table below shows honest per-vendor status. We <em>don&rsquo;t</em> sign a
            BAA with vendors who only handle billing identifiers or non-PHI data — but
            they&rsquo;re listed here anyway so procurement teams can audit the full
            chain.
          </p>
          <div className="cn-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subprocessor-table">
                <thead>
                  <tr className="border-b border-cn-border bg-cn-bone-100/40 dark:bg-cn-ink-800/30">
                    <th className="text-left px-4 py-3 font-semibold text-cn-text">Vendor</th>
                    <th className="text-left px-4 py-3 font-semibold text-cn-text">Purpose</th>
                    <th className="text-left px-4 py-3 font-semibold text-cn-text">Region</th>
                    <th className="text-left px-4 py-3 font-semibold text-cn-text">BAA</th>
                    <th className="text-left px-4 py-3 font-semibold text-cn-text">Touches PHI?</th>
                  </tr>
                </thead>
                <tbody>
                  {SUBPROCESSORS.map((s, idx) => (
                    <tr
                      key={s.name}
                      className={`border-b border-cn-border last:border-0 ${idx % 2 === 1 ? 'bg-cn-bone-50/30 dark:bg-cn-ink-800/20' : ''}`}
                      data-testid={`subprocessor-row-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <td className="px-4 py-3 font-medium text-cn-text">{s.name}</td>
                      <td className="px-4 py-3 text-cn-text-muted">{s.purpose}</td>
                      <td className="px-4 py-3 text-cn-text-muted font-mono text-xs">{s.region}</td>
                      <td className="px-4 py-3 text-cn-text">{s.baa}</td>
                      <td className="px-4 py-3 text-cn-text">{s.phi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-cn-text-muted/80 mt-4">
            Last reviewed: Feb 24, 2026. Customers under contract receive a 30-day advance email
            when a new subprocessor is added.
          </p>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="cn-section py-12 bg-cn-primary text-white">
        <div className="cn-container max-w-5xl flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <div className="cn-eyebrow mb-2 text-cn-accent">Have a question?</div>
            <h3 className="font-display font-bold text-xl text-white">
              We answer procurement RFPs within 48 hours.
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/ai-safety"
              className="cn-btn h-11 px-5 bg-white text-cn-primary hover:bg-cn-bone-100 inline-flex items-center gap-2"
              data-testid="trust-cta-ai-safety"
            >
              <BookOpen className="w-4 h-4" />
              AI Safety Posture
            </Link>
            <Link
              to="/status"
              className="cn-btn h-11 px-5 bg-cn-primary text-white border border-white/40 hover:bg-cn-primary/80 inline-flex items-center gap-2"
              data-testid="trust-cta-status"
            >
              <Server className="w-4 h-4" />
              System Status
            </Link>
            <a
              href="mailto:trust@caraloom.com"
              className="cn-btn h-11 px-5 bg-cn-accent text-cn-primary hover:bg-cn-accent/90 inline-flex items-center gap-2"
              data-testid="trust-cta-contact"
            >
              Email Trust Team <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* HONESTY FOOTNOTE */}
      <section className="cn-section py-12">
        <div className="cn-container max-w-3xl">
          <div className="cn-card p-6 flex items-start gap-4 bg-amber-50/40 border-amber-200" data-testid="honesty-note">
            <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
            <div>
              <h4 className="font-semibold text-cn-text mb-1">Why this page is dull</h4>
              <p className="text-sm text-cn-text-muted leading-relaxed">
                You won&rsquo;t find a &ldquo;Trusted by 200+ enterprises&rdquo; badge on this page because we
                aren&rsquo;t there yet. CaraLoom is early. What we <em>do</em> have is an honest BAA chain,
                a funded SOC 2 engagement, and an audit-ready vendor packet you can stress-test
                today. If we mislead procurement now, we lose the renewal in year two.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
